import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useCallback, useRef, useEffect } from "react";
import type { CampaignDetail, Offer } from "@/lib/hiring-api";
import {
  useGetCampaign,
  useUpdateOffer,
  useResendOffer,
  useSendFollowUp,
  type OfferStatusFilter,
} from "@/lib/hiring-api";
import { useQueryClient, useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { logUpdate, logCreate } from "@/lib/audit-log";
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  Search,
  Users,
  CheckCircle2,
  Package,
  Video,
  MoreHorizontal,
  RefreshCw,
  Loader2,
  Mail,
  Send,
  Trophy,
  TrendingUp,
  BarChart2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WIDE_TABLE_SCROLL,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-lg border bg-card p-4">
      <div className="rounded-md bg-muted p-2">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

interface LeaderboardRow {
  id: string;
  hiring_campaign_id: string;
  rank: number;
  creator_id: string | null;
  tiktok_username: string | null;
  creator_name: string | null;
  gmv: number;
  video_count: number;
  views: number;
  orders: number;
  computed_at: string;
}

const RANK_MEDALS: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

function fmt(n: number) {
  return "$" + Math.round(n).toLocaleString();
}
function fmtNum(n: number) {
  return Math.round(n).toLocaleString();
}

export default function CampaignDetail() {
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"pipeline" | "leaderboard">("pipeline");
  const [pipelineTab, setPipelineTab] = useState<OfferStatusFilter>("all");
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offersPage, setOffersPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(pipelineSearch.trim());
      setOffersPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [pipelineSearch]);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTargetOfferIds, setEmailTargetOfferIds] = useState<string[] | null>(null);
  const [emailTargetLabel, setEmailTargetLabel] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const { data: campaign, isLoading, isFetching } = useGetCampaign(
    id,
    offersPage,
    pipelineTab !== "all" ? pipelineTab : undefined,
    debouncedSearch || undefined,
    { query: { placeholderData: keepPreviousData } },
  );

  const isRewardsCampaign = !!(campaign as any)?.is_rewards_campaign;

  const { data: leaderboard = [], isLoading: leaderboardLoading } = useQuery<LeaderboardRow[]>({
    queryKey: ["leaderboard", id],
    queryFn: async () => {
      const res = await fetch(`/api/v1/hiring/campaigns/${id}/leaderboard`);
      if (!res.ok) throw new Error("Failed to fetch leaderboard");
      return res.json();
    },
    enabled: !!id && isRewardsCampaign && activeTab === "leaderboard",
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/hiring/campaigns/${id}/refresh-leaderboard`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to refresh");
      }
      return res.json() as Promise<LeaderboardRow[]>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["leaderboard", id], data);
      toast({ title: "Rankings updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to refresh rankings", description: err.message, variant: "destructive" });
    },
  });

  const updateOffer = useUpdateOffer({
    mutation: {
      onSuccess: (_, variables) => {
        logUpdate('hiring_offers', variables.id, {}, variables.data as Record<string, any>, { campaign_id: id });
        queryClient.invalidateQueries({ queryKey: [`/api/v1/hiring/campaigns/${id}`] });
      },
      onError: () => {
        toast({ title: "Failed to update offer", variant: "destructive" });
      },
    },
  });

  const resendOffer = useResendOffer({
    mutation: {
      onSuccess: (_, variables) => {
        logUpdate('hiring_offers', variables.id, {}, { email_resent: true, resent_at: new Date().toISOString() }, { campaign_id: id });
        toast({ title: "Email resent successfully" });
        setResendingId(null);
      },
      onError: () => {
        toast({ title: "Failed to resend email", variant: "destructive" });
        setResendingId(null);
      },
    },
  });

  const sendFollowUp = useSendFollowUp({
    mutation: {
      onSuccess: (data, variables) => {
        const result = data as unknown as { sent: number; failed: number };
        logCreate('hiring_follow_ups', id, variables.data as Record<string, any>, { campaign_id: id, sent: result.sent, failed: result.failed });
        const msg = result.failed > 0
          ? `Sent to ${result.sent}, failed ${result.failed}`
          : `Email sent to ${result.sent} creator${result.sent !== 1 ? "s" : ""}`;
        toast({ title: msg });
        setSendingEmail(false);
        setEmailDialogOpen(false);
        resetEmailForm();
      },
      onError: () => {
        toast({ title: "Failed to send email", variant: "destructive" });
        setSendingEmail(false);
      },
    },
  });

  function handleToggle(
    offerId: string,
    field: "sample_requested" | "sample_shipped" | "videos_received",
    value: boolean | number
  ) {
    updateOffer.mutate({ id: offerId, data: { [field]: value } });
  }

  function handleResend(offerId: string) {
    setResendingId(offerId);
    resendOffer.mutate({ id: offerId });
  }

  function resetEmailForm() {
    setEmailSubject("");
    setEmailBody("");
    setEmailTargetOfferIds(null);
    setEmailTargetLabel("");
  }

  function openEmailDialog(offerIds: string[] | null, label: string) {
    setEmailTargetOfferIds(offerIds);
    setEmailTargetLabel(label);
    setEmailSubject("");
    setEmailBody("");
    setEmailDialogOpen(true);
  }

  function handleSendFollowUp() {
    if (!emailSubject.trim() || !emailBody.trim()) return;
    setSendingEmail(true);
    sendFollowUp.mutate({
      id,
      data: {
        offer_ids: emailTargetOfferIds ?? undefined,
        subject: emailSubject,
        body: emailBody,
      },
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-md" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p>Campaign not found.</p>
        <Button variant="link" onClick={() => navigate("/hiring")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const detail = campaign as unknown as CampaignDetail;
  const allOffers: Offer[] = detail.offers ?? [];
  const pagination = detail.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const totalOffers = pagination?.total ?? allOffers.length;
  const pageLimit = pagination?.limit ?? allOffers.length;
  const offers = allOffers;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
          <Link to="/hiring">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
        </Button>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{detail.brand_name}</h1>
          {detail.product_name && (
            <p className="text-muted-foreground text-sm mt-0.5">{detail.product_name}</p>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-sm font-medium text-green-700 bg-green-500/10 px-2 py-0.5 rounded-full">
              ${detail.offer_amount}/video
            </span>
            <span className="text-sm text-muted-foreground">
              {detail.num_videos_per_creator} videos per creator
            </span>
            {detail.max_spots && (
              <span className="text-sm text-muted-foreground">
                Max {detail.max_spots} spots
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {offers.length > 0 && (
            <Button
              variant="outline"
              onClick={() => openEmailDialog(null, `all ${offers.length} creators`)}
              data-testid="btn-email-all"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email All
            </Button>
          )}
          <Button variant="outline" asChild data-testid="btn-find-creators">
            <Link to={`/hiring/campaigns/${id}/find`}>
              <Users className="h-4 w-4 mr-2" />
              Find Creators
            </Link>
          </Button>
          <Button variant="outline" asChild data-testid="btn-edit-campaign">
            <Link to={`/hiring/campaigns/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Offers Sent" value={detail.offers_sent ?? 0} />
        <StatCard icon={CheckCircle2} label="Accepted" value={detail.offers_accepted ?? 0} />
        <StatCard icon={Package} label="Samples Shipped" value={detail.samples_shipped ?? 0} />
        <StatCard icon={Video} label="Videos Received" value={detail.total_videos_received ?? 0} />
      </div>

      {/* Tab bar */}
      <div className="flex border-b">
        <button
          type="button"
          onClick={() => setActiveTab("pipeline")}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === "pipeline" ? "border-b-2 border-[#FB3AAE] text-[#FB3AAE]" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Users className="h-4 w-4" />
          Pipeline
        </button>
        {isRewardsCampaign && (
          <button
            type="button"
            onClick={() => setActiveTab("leaderboard")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === "leaderboard" ? "border-b-2 border-[#FB3AAE] text-[#FB3AAE]" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Trophy className="h-4 w-4" />
            Leaderboard
          </button>
        )}
      </div>

      {/* Pipeline tab */}
      {activeTab === "pipeline" && (
      <>
      {/* Pipeline status sub-tabs */}
      <div className="flex items-center gap-1 border-b">
        {(["all", "sent", "accepted"] as OfferStatusFilter[]).map((tab) => {
          const count =
            tab === "all"       ? (detail.offers_sent ?? 0)
            : tab === "accepted"  ? (detail.offers_accepted ?? 0)
            : Math.max(0, (detail.offers_sent ?? 0) - (detail.offers_accepted ?? 0));
          return (
            <button
              key={tab}
              type="button"
              onClick={() => { setPipelineTab(tab); setOffersPage(1); }}
              className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 capitalize ${
                pipelineTab === tab
                  ? "border-b-2 border-[#FB3AAE] text-[#FB3AAE]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab}
              <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${
                pipelineTab === tab ? "bg-[#FB3AAE]/10 text-[#FB3AAE]" : "bg-muted text-muted-foreground"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            value={pipelineSearch}
            onChange={e => setPipelineSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
      </div>

      {offers.length === 0 ? (
        <div className="rounded-md border bg-card flex flex-col items-center justify-center py-16 text-muted-foreground text-sm">
          <Users className="h-10 w-10 mb-4 opacity-30" />
          <p className="font-medium">
            {debouncedSearch
              ? "No creators match your search"
              : pipelineTab === "accepted"
              ? "No creators have accepted yet"
              : "No offers sent yet"}
          </p>
          <p className="mt-1">
            {debouncedSearch
              ? "Try a different name, email, or TikTok handle."
              : pipelineTab === "accepted"
              ? "Accepted creators will appear here once they respond to their invite."
              : "Use \"Find Creators\" to browse your roster and send offers."}
          </p>
        </div>
      ) : (
        <div className={`rounded-md border bg-card overflow-x-auto transition-opacity duration-150 ${isFetching ? "opacity-60 pointer-events-none" : ""}`}>
          {/* Exclusive Campaigns tables keep the old scroll-sideways layout. */}
          <Table className={WIDE_TABLE_SCROLL}>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead className="text-right">Creator GMV</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Accepted</TableHead>
                <TableHead className="text-center">Sample Req.</TableHead>
                <TableHead className="text-center">Videos</TableHead>
                <TableHead className="text-center">Email</TableHead>
                <TableHead className="w-[48px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offers?.map((offer) => (
                <TableRow key={offer.id} className="group">
                  <TableCell>
                    <div className="font-medium">
                      {offer.creator_name ?? offer.creator_tiktok ?? "Unknown"}
                    </div>
                    {offer.creator_tiktok && offer.creator_name && (
                      <div className="text-xs text-muted-foreground">
                        @{offer.creator_tiktok}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {offer.creator_email}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    {offer.creator_gmv != null
                      ? "$" + Math.round(Number(offer.creator_gmv)).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {offer.status === "accepted" ? (
                      <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                        Accepted
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-amber-700 bg-amber-500/10 border-amber-500/20"
                      >
                        Sent
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {offer.accepted_at
                      ? new Date(offer.accepted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded cursor-pointer accent-green-600"
                      checked={offer.sample_requested}
                      disabled={offer.status !== "accepted"}
                      onChange={(e) =>
                        handleToggle(offer.id, "sample_requested", e.target.checked)
                      }
                      data-testid={`checkbox-sample-req-${offer.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <input
                      type="number"
                      min={0}
                      className="w-14 text-center border rounded px-1 py-0.5 text-sm bg-background disabled:opacity-40"
                      value={offer.videos_received}
                      disabled={!offer.sample_requested}
                      onChange={(e) =>
                        handleToggle(
                          offer.id,
                          "videos_received",
                          Number(e.target.value)
                        )
                      }
                      data-testid={`input-videos-received-${offer.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        openEmailDialog(
                          [offer.id],
                          offer.creator_name ?? offer.creator_tiktok ?? "this creator"
                        )
                      }
                      data-testid={`btn-email-${offer.id}`}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleResend(offer.id)}
                          disabled={resendingId === offer.id}
                        >
                          {resendingId === offer.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          Resend Offer Email
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            openEmailDialog(
                              [offer.id],
                              offer.creator_name ?? offer.creator_tiktok ?? "this creator"
                            )
                          }
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Send Follow-up
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {(offersPage - 1) * pageLimit + 1}–{Math.min(offersPage * pageLimit, totalOffers)} of {totalOffers}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setOffersPage(p => Math.max(1, p - 1))}
              disabled={offersPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 tabular-nums">{offersPage} / {totalPages}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setOffersPage(p => Math.min(totalPages, p + 1))}
              disabled={offersPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      </>
      )}

      {/* Leaderboard tab */}
      {activeTab === "leaderboard" && (
        <div className="space-y-4">
          {leaderboardLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="rounded-md border bg-card flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-3">
              <Trophy className="h-10 w-10 opacity-30" />
              <p className="font-medium">No rankings yet.</p>
              <p>Click "Refresh Rankings" to compute.</p>
              <Button
                variant="outline"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
              >
                {refreshMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh Rankings
              </Button>
            </div>
          ) : (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg border bg-card p-4 flex items-center gap-4">
                  <div className="rounded-md bg-muted p-2">
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {fmt(leaderboard.reduce((s, r) => s + Number(r.gmv), 0))}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Campaign GMV</div>
                  </div>
                </div>
                <div className="rounded-lg border bg-card p-4 flex items-center gap-4">
                  <div className="rounded-md bg-muted p-2">
                    <Video className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {fmtNum(leaderboard.reduce((s, r) => s + Number(r.video_count), 0))}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Videos Tracked</div>
                  </div>
                </div>
                <div className="rounded-lg border bg-card p-4 flex items-center gap-4">
                  <div className="rounded-md bg-muted p-2">
                    <BarChart2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{leaderboard.length}</div>
                    <div className="text-sm text-muted-foreground">Creators Ranked</div>
                  </div>
                </div>
              </div>

              {/* Last updated + refresh */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Last updated:{" "}
                  {leaderboard[0]?.computed_at
                    ? new Date(leaderboard[0].computed_at).toLocaleString()
                    : "—"}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                >
                  {refreshMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh Rankings
                </Button>
              </div>

              {/* Rankings table */}
              <div className="rounded-md border bg-card overflow-x-auto">
                {/* Exclusive Campaigns tables keep the old scroll-sideways layout. */}
                <Table className={WIDE_TABLE_SCROLL}>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="w-16">Rank</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                      <TableHead className="text-right">Videos</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard.map((row) => (
                      <TableRow key={row.id} className={row.rank <= 3 ? "bg-yellow-50/30" : ""}>
                        <TableCell className="font-bold text-base">
                          {RANK_MEDALS[row.rank] ?? row.rank}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="font-medium">
                            {row.creator_name ?? row.tiktok_username ?? "Unknown"}
                          </div>
                          {row.tiktok_username && row.creator_name && (
                            <div className="text-xs text-muted-foreground">
                              @{row.tiktok_username}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-700">
                          {fmt(row.gmv)}
                        </TableCell>
                        <TableCell className="text-right">{fmtNum(row.video_count)}</TableCell>
                        <TableCell className="text-right">{fmtNum(row.views)}</TableCell>
                        <TableCell className="text-right">{fmtNum(row.orders)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={emailDialogOpen} onOpenChange={(open) => { if (!open) { setEmailDialogOpen(false); resetEmailForm(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              {emailTargetOfferIds
                ? `Compose an email to ${emailTargetLabel}.`
                : `Compose an email to ${emailTargetLabel} in this campaign.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject..."
                data-testid="input-followup-subject"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Message</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Write your message here..."
                className="min-h-[160px]"
                data-testid="input-followup-body"
              />
              <p className="text-xs text-muted-foreground">
                Use <code className="bg-muted px-1 py-0.5 rounded text-xs">{"{name}"}</code> to insert each creator's name automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEmailDialogOpen(false); resetEmailForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSendFollowUp}
              disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
              data-testid="btn-send-followup"
            >
              {sendingEmail ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
